import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@ngneat/transloco';
import { ApolloError } from 'apollo-client';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { QuestionnaireRequest, QuestionnaireType, SRFaxStatus } from 'src/API';
import { SideSheetOptions } from 'src/app/shared/side-sheet/side-sheet.service';
import { ClinicService } from 'src/app/core/api/clinic.service';
import { ErrorHandlerService } from 'src/app/core/api/error-handler.service';
import {
  getConsentFormStatus,
  isConsentQuestionnaire
} from 'src/app/core/api/consent-form.util';
import { QueueFaxResult } from 'src/app/core/api/fax.service';
import { safeJsonParse } from 'src/app/core/api/general.util';
import { FormTemplateGeneratorService } from 'src/app/form-template/form-template-generator/form-template-generator.service';
import { ClinicSetupService } from 'src/app/logged-in-navbar/clinic-setup-modal/clinic-setup.service';
import { Question } from 'src/app/questionnaires/speedII/speed-ii.model';
import { PhonePipe } from 'src/app/shared/shared-pipes/phone.pipe';
import { QuestionnaireScorePipe } from 'src/app/shared/shared-pipes/questionnaire-score.pipe';
import { ConfirmModalService } from '../../confirm-modal/confirm-modal.service';
import { LoadingSpinnerService } from '../../loading-spinner/loading-spinner.service';
import { Medication } from '../../medication-lookup/medication.model';
import { defaultIfNullish } from '../../util/helper.util';
import {
  InstrumentScore,
  QuestionnaireRequestService
} from './../../../core/api/questionnaire-request.service';
import { FormTemplateInputFormGroup } from './../../../form-template/form-template-input-form-group';
import { DEQ5Service } from './../../../questionnaires/deq5/deq5.service';
import { OSDIService } from './../../../questionnaires/osdi/osdi.service';
import { QuestionnairePdfExportService } from './../../../questionnaires/questionnaire-pdf-export.service';
import { SPEEDService } from './../../../questionnaires/speed/speed.service';
import { SPEEDIIService } from './../../../questionnaires/speedII/speed-ii.service';

export interface QuestionnaireResponseModalData {
  questionnaire: Partial<QuestionnaireRequest>;
  questionnaires: Partial<QuestionnaireRequest>[];
  initialComponentQuestionnaireTypeToShow?: string;
  patientId?: string;
}

interface QuestionAndResponse {
  title: string;
  response: any;
  label: string;
}

interface QuestionAndResponseGroup {
  questionsAndResponses: QuestionAndResponse[];
  title: string;
}

interface QuestionnaireScoreCard {
  instrumentLabel: string;
  scoreLabel: string;
  categoryLabel: string;
  thresholdLabel: string;
  riskLabel: string;
  hasRiskVerdict: boolean;
  isAtRisk: boolean;
}

interface QuestionsResponseData {
  questionsAndResponsesGroup?: QuestionAndResponseGroup[];
  totalScore?: string;
  medications?: Medication[];
  formGroupWithValues?: FormGroup;
  rawResponse?: any;
  schema?: any;
  scoreCards?: QuestionnaireScoreCard[];
  completedAt?: string;
  isConsentDeclined?: boolean;
}

interface QuestionnaireTabEntry {
  key: string;
  value: QuestionsResponseData;
  isPdfDownloadable: boolean;
}

@Component({
  selector: 'csi-questionnaire-response-modal',
  templateUrl: './questionnaire-response-modal.component.html',
  styleUrls: ['./questionnaire-response-modal.component.scss'],
  providers: [DEQ5Service, OSDIService, SPEEDService, SPEEDIIService, QuestionnaireScorePipe]
})
export class QuestionnaireResponseModalComponent implements OnInit {
  static readonly sideSheetOptions: SideSheetOptions = { width: '60vw', isFillHeight: true };

  public selectedIndex = 0;
  public serviceTypeMap = {
    [QuestionnaireType.DEQ]: this.deq5Service,
    [QuestionnaireType.OSDI]: this.osdiService,
    [QuestionnaireType.SPEED]: this.speedService,
    [QuestionnaireType.SPEEDII]: this.speedIIService
  };

  public questionsResponseData: {
    [key in QuestionnaireType]?: QuestionsResponseData;
  };

  public medicationControl: FormControl = new FormControl();
  public downloadingPDF = false;
  public isSendingFax = false;
  public readonly emrFaxNumber$: Observable<string> = this.clinicService
    .getEMRIntegrationFaxNumber(this.clinicSetupService.clinicId)
    .pipe(
      map(faxNumber => this.phonePipe.transform(faxNumber)),
      catchError(() => of(null)),
      shareReplay(1)
    );

  private readonly riskDirectionSymbols = {
    above: '>',
    atOrAbove: '≥',
    atOrBelow: '≤'
  };

  public tabEntries: QuestionnaireTabEntry[] = [];
  public hasMultipleTabs = false;
  public recordTypeLabel = 'Questionnaires';
  public completedAt = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: QuestionnaireResponseModalData,
    public questionnaireRequestService: QuestionnaireRequestService,
    private formTemplateGeneratorService: FormTemplateGeneratorService,
    public deq5Service: DEQ5Service,
    public speedService: SPEEDService,
    public osdiService: OSDIService,
    public speedIIService: SPEEDIIService,
    public questionnaireScorePipe: QuestionnaireScorePipe,
    private questionnairePdfExportService: QuestionnairePdfExportService,
    private loadingSpinnerService: LoadingSpinnerService,
    private errorHandlerService: ErrorHandlerService,
    private sheetReference: MatDialogRef<QuestionnaireResponseModalComponent>,
    private clinicService: ClinicService,
    private clinicSetupService: ClinicSetupService,
    private confirmModalService: ConfirmModalService,
    private snackBar: MatSnackBar,
    private translocoService: TranslocoService,
    private phonePipe: PhonePipe
  ) {}

  public static buildDialogData(
    questionnaire: Partial<QuestionnaireRequest>,
    questionnaires: Partial<QuestionnaireRequest>[] = null,
    patientId?: string,
    initialComponentQuestionnaireTypeToShow?: string
  ): QuestionnaireResponseModalData {
    return {
      questionnaire,
      questionnaires,
      patientId: patientId || null,
      initialComponentQuestionnaireTypeToShow
    };
  }

  static open(
    dialog: MatDialog,
    questionnaire: QuestionnaireRequest,
    questionnaires: QuestionnaireRequest[] = null,
    patientId?: string,
    initialComponentQuestionnaireTypeToShow?: string
  ): MatDialogRef<QuestionnaireResponseModalComponent> {
    return dialog.open(QuestionnaireResponseModalComponent, {
      data: QuestionnaireResponseModalComponent.buildDialogData(
        questionnaire,
        questionnaires,
        patientId,
        initialComponentQuestionnaireTypeToShow
      ),
      width: '80vw'
    });
  }
  ngOnInit() {
    // For singular questionnaires
    if (this.data.questionnaire) {
      const type = this.data.questionnaire.type;

      this.questionsResponseData = this.getQuestionAndResponseForQuestionnaireType(
        type,
        this.data.questionnaire
      );
      this.attachScoreCards(this.questionsResponseData, this.data.questionnaire);

      // For OSIDAndSPEED or SPEEDII to display the tab of the specified component questionnaire
      if (this.data.initialComponentQuestionnaireTypeToShow) {
        const componentQuestionnaireType = this.data.initialComponentQuestionnaireTypeToShow.toUpperCase();

        if (type === QuestionnaireType.OSDIAndSPEED) {
          if (componentQuestionnaireType === QuestionnaireType.SPEED) {
            this.selectedIndex = 1;
          }
        } else if (type === QuestionnaireType.SPEEDII) {
          if (componentQuestionnaireType === QuestionnaireType.SPEEDII) {
            this.selectedIndex = 1;
          }
        }
      }
    }

    // For multiple questionnaires
    if (this.data.questionnaires) {
      const latestQuestionnaireCompletedAt = {};
      this.questionsResponseData = {};
      this.data.questionnaires.forEach(currentQuestionnaire => {
        const currentQuestionnaireCompletedAt = new Date(
          currentQuestionnaire.completedAt
        ).getTime();

        let questionnaireType = currentQuestionnaire.type;
        if (questionnaireType === 'CUSTOM') {
          questionnaireType =
            (currentQuestionnaire.config as any) instanceof Object
              ? currentQuestionnaire.config['schema']['abbreviation']
              : JSON.parse(currentQuestionnaire.config).schema.abbreviation;
        }
        if (
          !latestQuestionnaireCompletedAt[questionnaireType] ||
          latestQuestionnaireCompletedAt[questionnaireType] < currentQuestionnaireCompletedAt
        ) {
          latestQuestionnaireCompletedAt[questionnaireType] = currentQuestionnaireCompletedAt;

          const questionnaireTabs = this.getQuestionAndResponseForQuestionnaireType(
            currentQuestionnaire.type,
            currentQuestionnaire
          );
          this.attachScoreCards(questionnaireTabs, currentQuestionnaire);

          this.questionsResponseData = {
            ...this.questionsResponseData,
            ...questionnaireTabs
          };
        }
      });
    }

    this.buildTabEntries();
  }

  public close(): void {
    this.sheetReference.close();
  }

  public trackByTabKey(index: number, tabEntry: QuestionnaireTabEntry): string {
    return tabEntry.key;
  }

  public trackByInstrumentLabel(index: number, scoreCard: QuestionnaireScoreCard): string {
    return scoreCard.instrumentLabel;
  }

  private attachScoreCards(
    questionnaireTabs: { [key: string]: QuestionsResponseData },
    questionnaire: Partial<QuestionnaireRequest>
  ): void {
    const scoreCards = this.buildScoreCards(questionnaire);
    Object.keys(questionnaireTabs).forEach(tabKey => {
      questionnaireTabs[tabKey].scoreCards = scoreCards;
      questionnaireTabs[tabKey].completedAt = questionnaire.completedAt;
    });
  }

  private toTotalScoreMarkup(questionnaire: Partial<QuestionnaireRequest>): string {
    const isScoreless =
      !isConsentQuestionnaire(questionnaire) &&
      !this.questionnaireRequestService.hasCategorizedScore(questionnaire as any);
    if (isScoreless) {
      return '';
    }
    return this.questionnaireScorePipe.transform(questionnaire, true);
  }

  private buildScoreCards(
    questionnaire: Partial<QuestionnaireRequest>
  ): QuestionnaireScoreCard[] {
    return this.questionnaireRequestService
      .getInstrumentScores(questionnaire as any)
      .filter(instrumentScore => !isNaN(instrumentScore.score))
      .map(instrumentScore => this.toScoreCard(instrumentScore));
  }

  private toThresholdLabel(instrumentType: string): string {
    const riskThreshold = this.questionnaireRequestService.instrumentRiskThresholds[
      instrumentType
    ];
    if (!riskThreshold) {
      return '';
    }
    return this.riskDirectionSymbols[riskThreshold.direction] + ' ' + riskThreshold.threshold;
  }

  private toScoreCard(instrumentScore: InstrumentScore): QuestionnaireScoreCard {
    const thresholdLabel = this.toThresholdLabel(instrumentScore.type);
    const hasRiskVerdict = !!thresholdLabel;
    return {
      instrumentLabel: instrumentScore.friendlyName,
      scoreLabel: String(instrumentScore.score),
      categoryLabel: instrumentScore.category,
      thresholdLabel: thresholdLabel,
      riskLabel: instrumentScore.isAtRisk ? 'At Risk' : 'Not At Risk',
      hasRiskVerdict: hasRiskVerdict,
      isAtRisk: hasRiskVerdict && instrumentScore.isAtRisk
    };
  }

  private buildTabEntries(): void {
    const tabKeys = Object.keys(this.questionsResponseData || {}).sort();

    this.tabEntries = tabKeys.map(tabKey => ({
      key: tabKey,
      value: this.questionsResponseData[tabKey],
      isPdfDownloadable:
        !!this.data.patientId &&
        !this.questionsResponseData[tabKey].isConsentDeclined &&
        tabKey !== QuestionnaireType.DERFS &&
        tabKey !== QuestionnaireType.CDERFS
    }));

    this.hasMultipleTabs = this.tabEntries.length > 1;
    this.recordTypeLabel =
      this.tabEntries.length === 1 ? this.tabEntries[0].key : 'Questionnaires';
    this.completedAt = this.toLatestCompletedAt();
    this.selectedIndex = Math.min(this.selectedIndex, Math.max(this.tabEntries.length - 1, 0));
  }

  private toLatestCompletedAt(): string {
    let latestCompletedAt = '';
    this.tabEntries.forEach(tabEntry => {
      const tabCompletedAt = tabEntry.value.completedAt;
      if (!tabCompletedAt) {
        return;
      }
      if (
        !latestCompletedAt ||
        new Date(tabCompletedAt).getTime() > new Date(latestCompletedAt).getTime()
      ) {
        latestCompletedAt = tabCompletedAt;
      }
    });
    return latestCompletedAt;
  }

  generatePDF({ key, value }: { key: string; value: any }) {
    // this.formTemplateGeneratorService.formatForPDF = true;
    this.loadingSpinnerService.show();
    this.downloadingPDF = true;
    const questionnaireKey =
      value.schema && value.schema.abbreviation ? value.schema.abbreviation : key;
    this.questionnairePdfExportService
      .downloadQuestionnaireAsPDF(questionnaireKey, value, this.data.patientId, false)
      .pipe(
        finalize(() => {
          this.loadingSpinnerService.hide();
          this.downloadingPDF = false;
          // this.formTemplateGeneratorService.formatForPDF = false;
        })
      )
      .subscribe({
        error: (error: ApolloError) => {
          this.errorHandlerService.handleGraphQLError(error, true);
        }
      });
  }

  sendToEFax({ key, value }: { key: string; value: any }, faxNumber: string) {
    const questionnaireKey =
      value.schema && value.schema.abbreviation ? value.schema.abbreviation : key;
    this.isSendingFax = true;
    this.confirmModalService
      .show(
        'Send to eFax?',
        'Are you sure you want to fax this document to {{faxNumber}}?',
        'Send',
        'Cancel',
        false,
        false,
        false,
        { bodyParams: { faxNumber } }
      )
      .pipe(
        take(1),
        switchMap(isConfirmed => {
          if (!isConfirmed) {
            this.isSendingFax = false;
            return EMPTY;
          }
          return this.questionnairePdfExportService
            .faxQuestionnaire(faxNumber, questionnaireKey, value, this.data.patientId)
            .pipe(finalize(() => (this.isSendingFax = false)));
        })
      )
      .subscribe({
        next: (faxResult: QueueFaxResult) => {
          const isFaxQueued = !!faxResult && faxResult.Status === SRFaxStatus.Success;
          if (!isFaxQueued) {
            this.errorHandlerService.handleError(`Failed to queue fax to ${faxNumber}`);
          }
          this.snackBar.open(
            this.translocoService.translate(
              isFaxQueued ? 'SNACKBARS.FAX_QUEUED' : 'SNACKBARS.FAX_FAILED',
              { faxNumber }
            ),
            this.translocoService.translate('SNACKBARS.CLOSE'),
            { duration: 4000 }
          );
        },
        error: (error: ApolloError) => {
          this.errorHandlerService.handleGraphQLError(error, true);
        }
      });
  }

  getQuestionAndResponseForQuestionnaireType(
    type: QuestionnaireType,
    questionnaire: Partial<QuestionnaireRequest>
  ) {
    const questionnaireConfig = safeJsonParse(defaultIfNullish(questionnaire.config, '{}'));
    if (type === QuestionnaireType.DERFS || type === QuestionnaireType.CDERFS) {
      return { [type]: { rawResponse: questionnaire.response } };
    } else if (type === QuestionnaireType.CUSTOM) {
      const parsedConfig =
        (questionnaire.config as any) instanceof Object
          ? questionnaire.config
          : JSON.parse(questionnaire.config || '{}');
      const customQuestionnaireFormGroup = new FormTemplateInputFormGroup(parsedConfig.schema);
      customQuestionnaireFormGroup.patchValue(JSON.parse(questionnaire.response.answers));
      return {
        [parsedConfig.schema.abbreviation]: {
          formGroupWithValues: customQuestionnaireFormGroup,
          schema: parsedConfig.schema,
          totalScore: this.toTotalScoreMarkup(questionnaire),
          completedAt: questionnaire.completedAt,
          isConsentDeclined:
            isConsentQuestionnaire(questionnaire) &&
            getConsentFormStatus(questionnaire) === 'rejected'
        }
      };
    } else if (
      Object.keys(this.questionnaireRequestService.formTemplateLibraryQuestionnaires).includes(
        type
      ) &&
      ((questionnaireConfig && questionnaireConfig['schema']) ||
        (!this.serviceTypeMap[type] && type !== QuestionnaireType.OSDIAndSPEED))
    ) {
      const questionnaireSchema = this.questionnaireRequestService
        .formTemplateLibraryQuestionnaires[type];
      const customQuestionnaireFormGroup = new FormTemplateInputFormGroup(questionnaireSchema);
      customQuestionnaireFormGroup.patchValue(JSON.parse(questionnaire.response.answers));
      return {
        [questionnaireSchema.abbreviation ||
        this.questionnaireRequestService.questionnaireTypeToFriendlyNameMap[type]]: {
          formGroupWithValues: customQuestionnaireFormGroup,
          schema: questionnaireSchema,
          totalScore: this.toTotalScoreMarkup(questionnaire),
          completedAt: questionnaire.completedAt
        }
      };
    } else if (type !== QuestionnaireType.OSDIAndSPEED && type !== QuestionnaireType.SPEEDII) {
      return {
        [type]: this.getQuestionAndResponse(type, questionnaire)
      };
    } else if (type === QuestionnaireType.SPEEDII) {
      return {
        [QuestionnaireType.SPEED]: this.getQuestionAndResponse(
          QuestionnaireType.SPEED,
          questionnaire
        ),
        [QuestionnaireType.SPEEDII]: this.getQuestionAndResponse(
          QuestionnaireType.SPEEDII,
          questionnaire
        )
      };
    } else {
      return {
        [QuestionnaireType.OSDI]: this.getQuestionAndResponse(
          QuestionnaireType.OSDI,
          questionnaire
        ),
        [QuestionnaireType.SPEED]: this.getQuestionAndResponse(
          QuestionnaireType.SPEED,
          questionnaire
        )
      };
    }
  }

  getQuestionAndResponse(
    type: QuestionnaireType,
    questionnaire: Partial<QuestionnaireRequest>
  ): QuestionsResponseData {
    const questionAndResponsesGroup: QuestionAndResponseGroup[] = [];
    const response = JSON.parse(questionnaire.response.answers)[type];
    const totalScore = this.toTotalScoreMarkup(questionnaire);
    const medications = response.patientEnteredMedications;
    const service = this.serviceTypeMap[type];

    if (medications) {
      this.medicationControl.setValue(medications);
      this.medicationControl.disable();
    }

    if (type === QuestionnaireType.SPEEDII) {
      [
        this.speedIIService.groupThreeQuestions,
        this.speedIIService.groupFourQuestions,
        this.speedIIService.groupFiveQuestions,
        this.speedIIService.groupSixQuestions
      ].forEach((questions: Question[]) => {
        const groupQuestionAndResponse: QuestionAndResponse[] = [];

        let groupTitle = '';

        questions.forEach(question => {
          groupTitle = question.group.title;

          const questionAndResponse = {
            title: question.title,
            label: '',
            response: response[question.id] || 'N/A'
          };
          const checkedResponses = [];
          if (question.fieldConfig.type === 'Checkbox') {
            Object.entries(question.fieldConfig.options).forEach(([key, value]) => {
              if (response[question.id + '-' + key]) {
                checkedResponses.push(value);
              }
            });
            questionAndResponse['response'] = checkedResponses.join(', ') || 'N/A';
          }
          groupQuestionAndResponse.push(questionAndResponse);
        });

        questionAndResponsesGroup.push({
          title: groupTitle,
          questionsAndResponses: groupQuestionAndResponse
        });
      });
      return { questionsAndResponsesGroup: questionAndResponsesGroup, totalScore };
    }

    service.questionGroups.forEach(questionGroup => {
      const questionsAndResponses: QuestionAndResponse[] = [];
      questionGroup.questions.forEach(question => {
        const questionResponse = response[questionGroup.id][question.id];

        questionsAndResponses.push({
          title: question.title,
          response: questionResponse.notApplicable ? 'N/A' : questionResponse.answer,
          label: questionResponse.notApplicable
            ? 'N/A'
            : question.labels[questionResponse.answer + 1]
        });
      });
      questionAndResponsesGroup.push({
        title: questionGroup.title,
        questionsAndResponses
      });
    });

    return {
      questionsAndResponsesGroup: questionAndResponsesGroup,
      totalScore,
      medications: medications ? medications : ''
    };
  }
}
